if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.productInfo = this.closest('product-info');
        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.livePreviewV3Coordinator ||= this.productInfo?.querySelector('ccm-live-preview-v3');
        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        window.StorefrontRequest.json(`${routes.cart_add_url}`, config, { timeoutMs: 30000 })
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            }
            if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            return window.StorefrontRequest.json(`${routes.cart_url}.js`, { headers: { Accept: 'application/json' } })
              .then((responseCart) => {
                if (quickAddModal) {
                  document.body.addEventListener(
                    'modalClosed',
                    () => {
                      setTimeout(() => {
                        CartPerformance.measure("add:paint-updated-sections", () => {
                          this.cart.renderContents(response, responseCart);
                        });
                      });
                    },
                    { once: true }
                  );
                  quickAddModal.hide(true);
                } else {
                  CartPerformance.measure("add:paint-updated-sections", () => {
                    this.cart.renderContents(response, responseCart);
                  });
                }
              })
          })
          .catch(() => {
            // Shopify may have accepted the add even if its response was lost.
            // Check the authoritative cart; never automatically repeat the add.
            window.location.assign(window.routes.cart_url);
          })
          .finally(() => {
            this.livePreviewV3Coordinator?.releaseSubmissionUi?.();
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text && this.submitButtonText) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          if (this.submitButtonText) this.submitButtonText.textContent = window.variantStrings.addToCart;
        }

        if (this.productInfo) {
          this.productInfo.querySelectorAll('button[type="submit"][form]').forEach((button) => {
            const buttonLabel = button.querySelector('.product-form__submit-label');
            if (disable) {
              button.setAttribute('disabled', 'disabled');
              if (text) {
                if (buttonLabel) buttonLabel.textContent = text;
                else button.textContent = text;
              }
            } else {
              button.removeAttribute('disabled');
              if (buttonLabel) buttonLabel.textContent = window.variantStrings.addToCart;
              else button.textContent = window.variantStrings.addToCart;
            }
          })
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}

// Custom-order consent must be accepted before any add-to-cart work starts. Capture on document so this runs
// ahead of the photo-upload and live-preview submit interceptors registered on the form itself.
if (!window.ccmCustomOrderConsentBound) {
  window.ccmCustomOrderConsentBound = true;
  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      // Read the attribute: product forms contain an input named "id", which shadows form.id.
      const formId = form instanceof HTMLFormElement ? form.getAttribute('id') : null;
      if (!formId) return;
      const consent = document.querySelector(`[data-custom-order-consent][form="${CSS.escape(formId)}"]`);
      if (!consent || consent.checked) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      // Native validation bubbles are easy to miss on phones, so also show the message next to the checkbox.
      const label = consent.closest('label');
      let message = label?.nextElementSibling?.matches('[data-custom-order-consent-error]') ? label.nextElementSibling : null;
      if (label && !message) {
        message = document.createElement('p');
        message.className = 'pdp-v2-consent__error';
        message.dataset.customOrderConsentError = '';
        message.setAttribute('role', 'alert');
        label.after(message);
      }
      if (message) message.textContent = consent.dataset.errorMessage || '';
      label?.classList.add('is-invalid');
      (label || consent).scrollIntoView({ behavior: 'smooth', block: 'center' });
      consent.setCustomValidity(consent.dataset.errorMessage || '');
      consent.reportValidity();
    },
    true
  );
  document.addEventListener('change', (event) => {
    if (!event.target.matches?.('[data-custom-order-consent]')) return;
    event.target.setCustomValidity('');
    const label = event.target.closest('label');
    label?.classList.remove('is-invalid');
    if (label?.nextElementSibling?.matches('[data-custom-order-consent-error]')) label.nextElementSibling.remove();
  });
}
